import {
   chain,
   isEmpty,
   isNull,
   isUndefined,
   sortBy,
   values,
   map,
} from 'lodash';
import {
   DASHBOARD_TOP_TABS_VALUE,
   PERCENTAGE_ABSOLUTE_TABS_VALUE,
} from '../../../../utils/constants.js';
import { DateUtil } from '../../../../utils/date-util.js';
import { setNumber } from '../../../../utils/number-util.js';
import {
   getAggregationDateOrTimeStampString,
   NO_OF_CHANGED_FARES_KEY,
} from '../charts-util.js';
import {
   AVG_CHANGE_KEY,
   MAX_CHANGE_KEY,
   MIN_CHANGE_KEY,
   MIN_CHANGE_PERCENT_KEY,
   AVG_CHANGE_PERCENT_KEY,
   MAX_CHANGE_PERCENT_KEY,
} from './chart-competition-fare-changes-config.js';

// for line graphs alone
const PICK_MIN_KEY = MIN_CHANGE_KEY;
const PICK_AVG_KEY = AVG_CHANGE_KEY;
const PICK_MAX_KEY = MAX_CHANGE_KEY;
const PICK_MIN_PERCENT_KEY = MIN_CHANGE_PERCENT_KEY;
const PICK_AVG_PERCENT_KEY = AVG_CHANGE_PERCENT_KEY;
const PICK_MAX_PERCENT_KEY = MAX_CHANGE_PERCENT_KEY;

export const getFareChangeDetailsForOnDs = (
   fareChangesSummaryList,
   pickedFareChange
) => {
   let transformedData = {};
   const carriers = new Set();

   const plotBars = chain(fareChangesSummaryList)
      .map((fc) => {
         const fareChangeValue = getFareOrFareChangeValue(
            fc,
            pickedFareChange,
            NO_OF_CHANGED_FARES_KEY
         );
         if (isIgnore(fareChangeValue)) {
            return;
         }

         const recordKey = fc.origin;
         const barChartKey = `${recordKey}_${fc.cabinCode}_${fc.carrierCode}`;
         carriers.add(fc.carrierCode);

         const fareChangeSumByBarChartKey =
            transformedData[recordKey] &&
            transformedData[recordKey][barChartKey]
               ? transformedData[recordKey][barChartKey] + fareChangeValue
               : fareChangeValue;

         if (isUndefined(transformedData[recordKey])) {
            transformedData[recordKey] = {
               label: fc.origin,
               origin: fc.origin,
               destination: fc.destination,
               [barChartKey]: fareChangeValue,
               [`${barChartKey}_absolute`]: fareChangeValue,
               [`${fc.cabinCode}_totalChanges`]: fareChangeValue,
            };
         } else {
            transformedData[recordKey] = {
               ...transformedData[recordKey],
               [barChartKey]: fareChangeSumByBarChartKey,
               [`${barChartKey}_absolute`]: fareChangeSumByBarChartKey,
               [`${fc.cabinCode}_totalChanges`]: transformedData[recordKey][
                  `${fc.cabinCode}_totalChanges`
               ]
                  ? transformedData[recordKey][`${fc.cabinCode}_totalChanges`] +
                    fareChangeValue
                  : fareChangeValue,
            };
         }

         return fareChangeValue === 0 ? undefined : barChartKey;
      })
      .uniq()
      .compact()
      .value();

   transformedData = chain(transformedData)
      .map((eachOriginData) => {
         map(plotBars, (eachBarChartKey) => {
            if (eachOriginData[eachBarChartKey]) {
               const cabinCode = eachBarChartKey.split('_')[1];
               eachOriginData[`${eachBarChartKey}_relative`] = setNumber(
                  (eachOriginData[eachBarChartKey] * 100) /
                     eachOriginData[`${cabinCode}_totalChanges`]
               );
            }
         });
         return eachOriginData;
      })
      .values()
      .sortBy(['origin', 'cabinCode'])
      .value();

   return {
      transformedData,
      plotBars: plotBars.sort(),
      stackedValues: Array.from(carriers),
   };
};

export const getFareChangeDetailsForODPairs = (
   fareChangesSummaryList,
   pickedFareChange,
   pickedOrigin,
   showFaresBy,
   relativeAbsolute
) => {
   const sumAndAverageCalculation = new SumAndAverageCalculation({
      isODPair: true,
   });
   let transformedData = {};
   const carriers = new Set();
   const cabinCarrier = {};
   const pickRespectiveDataKey =
      isEmpty(relativeAbsolute) ||
      PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE === relativeAbsolute
         ? showFaresBy
         : getRespectivePercentKey(showFaresBy);

   const plotBars = chain(fareChangesSummaryList)
      .filter((fc) => fc.origin === pickedOrigin && fc[pickedFareChange])
      .map((fc) => {
         const fareValue = getFareOrFareChangeValue(
            fc,
            pickedFareChange,
            pickRespectiveDataKey
         );
         if (isIgnore(fareValue)) {
            return;
         }

         const recordKey = `${fc.origin}_${fc.destination}`;
         const barChartKey = `${recordKey}_${fc.cabinCode}_${fc.carrierCode}`;
         carriers.add(fc.carrierCode);
         cabinCarrier[
            `${fc.cabinCode}_${fc.carrierCode}`
         ] = `${fc.cabinCode}_${fc.carrierCode}`;

         // avg calculation is different from others
         sumAndAverageCalculation.addToSum(fc, pickedFareChange, PICK_AVG_KEY);
         sumAndAverageCalculation.addToSum(
            fc,
            pickedFareChange,
            PICK_AVG_PERCENT_KEY
         );

         if (isUndefined(transformedData[recordKey])) {
            transformedData[recordKey] = {
               label: `${fc.origin} - ${fc.destination}`,
               origin: fc.origin,
               destination: fc.destination,
               [barChartKey]: fareValue,
               [`${barChartKey}_${PICK_MIN_KEY}`]: getFareOrFareChangeValue(
                  fc,
                  pickedFareChange,
                  PICK_MIN_KEY
               ),
               [`${barChartKey}_${PICK_MAX_KEY}`]: getFareOrFareChangeValue(
                  fc,
                  pickedFareChange,
                  PICK_MAX_KEY
               ),
               [`${barChartKey}_${PICK_MIN_PERCENT_KEY}`]:
                  getFareOrFareChangeValue(
                     fc,
                     pickedFareChange,
                     PICK_MIN_PERCENT_KEY
                  ),
               [`${barChartKey}_${PICK_MAX_PERCENT_KEY}`]:
                  getFareOrFareChangeValue(
                     fc,
                     pickedFareChange,
                     PICK_MAX_PERCENT_KEY
                  ),
            };
         } else {
            const min = getMinAvgMaxValue(
               transformedData[recordKey],
               `${barChartKey}_${PICK_MIN_KEY}`,
               PICK_MIN_KEY,
               getFareOrFareChangeValue(fc, pickedFareChange, PICK_MIN_KEY)
            );
            const max = getMinAvgMaxValue(
               transformedData[recordKey],
               `${barChartKey}_${PICK_MAX_KEY}`,
               PICK_MAX_KEY,
               getFareOrFareChangeValue(fc, pickedFareChange, PICK_MAX_KEY)
            );
            const minPercent = getMinAvgMaxValue(
               transformedData[recordKey],
               `${barChartKey}_${PICK_MIN_PERCENT_KEY}`,
               PICK_MIN_PERCENT_KEY,
               getFareOrFareChangeValue(
                  fc,
                  pickedFareChange,
                  PICK_MIN_PERCENT_KEY
               )
            );
            const maxPercent = getMinAvgMaxValue(
               transformedData[recordKey],
               `${barChartKey}_${PICK_MAX_PERCENT_KEY}`,
               PICK_MAX_PERCENT_KEY,
               getFareOrFareChangeValue(
                  fc,
                  pickedFareChange,
                  PICK_MAX_PERCENT_KEY
               )
            );

            transformedData[recordKey] = {
               ...transformedData[recordKey],
               [barChartKey]: getMinAvgMaxValue(
                  transformedData[recordKey],
                  barChartKey,
                  pickRespectiveDataKey,
                  fareValue
               ),
               [`${barChartKey}_${PICK_MIN_KEY}`]: min,
               [`${barChartKey}_${PICK_MAX_KEY}`]: max,
               [`${barChartKey}_${PICK_MIN_PERCENT_KEY}`]: minPercent,
               [`${barChartKey}_${PICK_MAX_PERCENT_KEY}`]: maxPercent,
            };
         }

         return fareValue === 0 ? undefined : barChartKey;
      })
      .uniq()
      .compact()
      .value();

   transformedData = chain(transformedData)
      .map((eachOriginData, prefixKey) => {
         if (AVG_CHANGE_KEY === showFaresBy) {
            map(cabinCarrier, (cabinCarrier) => {
               const partKey = `${prefixKey}_${cabinCarrier}`;
               eachOriginData[`${partKey}`] = sumAndAverageCalculation.getAvg(
                  `${partKey}_${pickRespectiveDataKey}`
               );
               eachOriginData[`${partKey}_${PICK_AVG_KEY}`] =
                  sumAndAverageCalculation.getAvg(`${partKey}_${PICK_AVG_KEY}`);
               eachOriginData[`${partKey}_${PICK_AVG_PERCENT_KEY}`] =
                  sumAndAverageCalculation.getAvg(
                     `${partKey}_${PICK_AVG_PERCENT_KEY}`
                  );
            });
         }

         return eachOriginData;
      })
      .values()
      .sortBy(['origin', 'destination', 'cabinCode'])
      .value();

   return {
      transformedData,
      plotBars: plotBars.sort(),
      stackedValues: Array.from(carriers),
   };
};

export const getFareChangeForMinAvgMax = (
   fareChangesSummaryList,
   pickedFareChange,
   pickedOrigin,
   pickedDestination,
   showFaresBy,
   cabinCode = null,
   relativeAbsolute = ''
) => {
   const sumAndAverageCalculation = new SumAndAverageCalculation({
      isMinAvgMax: true,
   });
   let transformedData = {};
   let cabinCodes = [];

   const plotLines = chain(fareChangesSummaryList)
      .filter(
         (fc) =>
            fc.origin === pickedOrigin &&
            fc.destination === pickedDestination &&
            fc[pickedFareChange]
      )
      .map((fc) => {
         cabinCodes[fc.cabinCode] = fc.cabinCode;
         const recordKey = getAggregationDateOrTimeStampString(
            fc,
            DASHBOARD_TOP_TABS_VALUE.COMPETITION,
            DateUtil.DATE_FORMATS.MONTH_NAME_DAY
         );
         fc.dateValue = recordKey;
         fc.sortByField = DateUtil.formatDate(
            recordKey,
            DateUtil.DATE_FORMATS.YEAR_MONTH_DAY_TIME
         );
         const lineChartKey = `${recordKey}_${fc.cabinCode}_${fc.carrierCode}`;

         // avg calculation is different from others
         sumAndAverageCalculation.addToSum(fc, pickedFareChange, PICK_AVG_KEY);
         sumAndAverageCalculation.addToSum(
            fc,
            pickedFareChange,
            PICK_AVG_PERCENT_KEY
         );
         const pickRespectiveMinDataKey =
            isEmpty(relativeAbsolute) ||
            PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE === relativeAbsolute
               ? PICK_MIN_KEY
               : getRespectivePercentKey(PICK_MIN_KEY);
         const pickRespectiveMaxDataKey =
            isEmpty(relativeAbsolute) ||
            PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE === relativeAbsolute
               ? PICK_MAX_KEY
               : getRespectivePercentKey(PICK_MAX_KEY);

         if (isUndefined(transformedData[recordKey])) {
            transformedData[recordKey] = {
               sortByField: fc.sortByField,
               dateValue: recordKey,
               [`${lineChartKey}_${PICK_MIN_KEY}`]:
                  fc[pickedFareChange][pickRespectiveMinDataKey],
               [`${lineChartKey}_${PICK_MIN_KEY}_${PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE}`]:
                  fc[pickedFareChange][PICK_MIN_KEY],
               [`${lineChartKey}_${PICK_MIN_PERCENT_KEY}`]:
                  fc[pickedFareChange][PICK_MIN_PERCENT_KEY],
               [`${lineChartKey}_${PICK_MAX_KEY}`]:
                  fc[pickedFareChange][pickRespectiveMaxDataKey],
               [`${lineChartKey}_${PICK_MAX_KEY}_${PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE}`]:
                  fc[pickedFareChange][PICK_MAX_KEY],
               [`${lineChartKey}_${PICK_MAX_PERCENT_KEY}`]:
                  fc[pickedFareChange][PICK_MAX_PERCENT_KEY],
            };
         } else {
            transformedData[recordKey] = {
               ...transformedData[recordKey],
               [`${lineChartKey}_${PICK_MIN_KEY}`]: getMinAvgMaxValue(
                  transformedData[recordKey],
                  `${lineChartKey}_${PICK_MIN_KEY}`,
                  PICK_MIN_KEY,
                  fc[pickedFareChange][pickRespectiveMinDataKey]
               ),
               [`${lineChartKey}_${PICK_MIN_KEY}_${PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE}`]:
                  getMinAvgMaxValue(
                     transformedData[recordKey],
                     `${lineChartKey}_${PICK_MIN_KEY}_${PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE}`,
                     PICK_MIN_KEY,
                     fc[pickedFareChange][PICK_MIN_KEY]
                  ),
               [`${lineChartKey}_${PICK_MIN_PERCENT_KEY}`]: getMinAvgMaxValue(
                  transformedData[recordKey],
                  `${lineChartKey}_${PICK_MIN_PERCENT_KEY}`,
                  PICK_MIN_PERCENT_KEY,
                  fc[pickedFareChange][PICK_MIN_PERCENT_KEY]
               ),
               [`${lineChartKey}_${PICK_MAX_KEY}`]: getMinAvgMaxValue(
                  transformedData[recordKey],
                  `${lineChartKey}_${PICK_MAX_KEY}`,
                  PICK_MAX_KEY,
                  fc[pickedFareChange][pickRespectiveMaxDataKey]
               ),
               [`${lineChartKey}_${PICK_MAX_KEY}_${PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE}`]:
                  getMinAvgMaxValue(
                     transformedData[recordKey],
                     `${lineChartKey}_${PICK_MAX_KEY}_${PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE}`,
                     PICK_MAX_KEY,
                     fc[pickedFareChange][PICK_MAX_KEY]
                  ),
               [`${lineChartKey}_${PICK_MAX_PERCENT_KEY}`]: getMinAvgMaxValue(
                  transformedData[recordKey],
                  `${lineChartKey}_${PICK_MAX_PERCENT_KEY}`,
                  PICK_MAX_PERCENT_KEY,
                  fc[pickedFareChange][PICK_MAX_PERCENT_KEY]
               ),
            };
         }

         return fc.carrierCode;
      })
      .uniq()
      .value();

   cabinCodes = sortBy(values(cabinCodes));
   const filterByCabinCode = cabinCode || cabinCodes[0];
   transformedData = chain(transformedData)
      .map((eachMinMaxAvgData) => {
         plotLines.map((carrierCode) => {
            const pickRespectiveMinDataKey =
               isEmpty(relativeAbsolute) ||
               PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE === relativeAbsolute
                  ? PICK_MIN_KEY
                  : getRespectivePercentKey(PICK_MIN_KEY);
            const pickRespectiveMaxDataKey =
               isEmpty(relativeAbsolute) ||
               PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE === relativeAbsolute
                  ? PICK_MAX_KEY
                  : getRespectivePercentKey(PICK_MAX_KEY);
            const pickRespectiveAvgDataKey =
               isEmpty(relativeAbsolute) ||
               PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE === relativeAbsolute
                  ? PICK_AVG_KEY
                  : getRespectivePercentKey(PICK_AVG_KEY);

            const keyPrefix = `${eachMinMaxAvgData.dateValue}_${filterByCabinCode}_${carrierCode}`;
            eachMinMaxAvgData[`${carrierCode}_${PICK_MIN_KEY}`] =
               eachMinMaxAvgData[`${keyPrefix}_${pickRespectiveMinDataKey}`];
            eachMinMaxAvgData[`${carrierCode}_${PICK_MAX_KEY}`] =
               eachMinMaxAvgData[`${keyPrefix}_${pickRespectiveMaxDataKey}`];
            eachMinMaxAvgData[`${carrierCode}_${PICK_AVG_KEY}`] =
               sumAndAverageCalculation.getAvg(
                  `${keyPrefix}_${pickRespectiveAvgDataKey}`
               );

            eachMinMaxAvgData[
               `${keyPrefix}_${PICK_AVG_KEY}_${PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE}`
            ] = sumAndAverageCalculation.getAvg(`${keyPrefix}_${PICK_AVG_KEY}`);
            eachMinMaxAvgData[`${keyPrefix}_${PICK_AVG_PERCENT_KEY}`] =
               sumAndAverageCalculation.getAvg(
                  `${keyPrefix}_${PICK_AVG_PERCENT_KEY}`
               );
         });
         return eachMinMaxAvgData;
      })
      .sortBy(['sortByField'])
      .value();

   return {
      transformedData,
      plotLines,
      cabinCodes,
   };
};

export const getAbsoluteOrRelativeData = (
   fareChangesSummaryList,
   plotType,
   relativeAbsolute,
   chartType,
   showFaresBy,
   cabinCode
) => {
   const { isOnDs = false, oDPairs = false, minAvgMax = false } = chartType;
   let minKey = `_${PICK_MIN_KEY}`;
   let avgKey = `_${PICK_AVG_KEY}`;
   let maxKey = `_${PICK_MAX_KEY}`;
   let odKey = showFaresBy;

   if (minAvgMax) {
      minKey = `${minKey}_${PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE}`;
      avgKey = `${avgKey}_${PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE}`;
      maxKey = `${maxKey}_${PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE}`;
   }

   if (PERCENTAGE_ABSOLUTE_TABS_VALUE.RELATIVE === relativeAbsolute) {
      minKey = `_${PICK_MIN_PERCENT_KEY}`;
      avgKey = `_${PICK_AVG_PERCENT_KEY}`;
      maxKey = `_${PICK_MAX_PERCENT_KEY}`;
      odKey = PICK_AVG_PERCENT_KEY;
      if (showFaresBy === PICK_MIN_KEY) {
         odKey = PICK_MIN_PERCENT_KEY;
      } else if (showFaresBy === PICK_MAX_KEY) {
         odKey = PICK_MAX_PERCENT_KEY;
      }
   }

   map(fareChangesSummaryList.transformedData, (fc) => {
      map(fareChangesSummaryList[plotType], (availableKey) => {
         if (isOnDs) {
            fc[availableKey] = fc[`${availableKey}_${relativeAbsolute}`];
         } else if (oDPairs && !isUndefined(fc[`${availableKey}_${odKey}`])) {
            fc[availableKey] = fc[`${availableKey}_${odKey}`];
         } else if (minAvgMax) {
            fc[`${availableKey}_${PICK_MIN_KEY}`] =
               fc[`${fc.dateValue}_${cabinCode}_${availableKey}${minKey}`];
            fc[`${availableKey}_${PICK_AVG_KEY}`] =
               fc[`${fc.dateValue}_${cabinCode}_${availableKey}${avgKey}`];
            fc[`${availableKey}_${PICK_MAX_KEY}`] =
               fc[`${fc.dateValue}_${cabinCode}_${availableKey}${maxKey}`];
         }
      });
      return fc;
   });

   return fareChangesSummaryList;
};

export const isIgnore = (valueToCheck) =>
   isUndefined(valueToCheck) ||
   isNull(valueToCheck) ||
   parseFloat(valueToCheck).toFixed(2) === '0.00';

export const getFareOrFareChangeValue = (fc, pickedFareChange, byKey) =>
   fc[pickedFareChange] && fc[pickedFareChange][byKey];

export const getFareOrFareChangeValueByKey = (fc, byKey) => fc[byKey];

export const getMinAvgMaxValue = (dataSet, dataKey, showFaresBy, dataValue) => {
   if (isIgnore(dataValue)) {
      return dataSet[dataKey];
   }

   let val = 0;

   switch (showFaresBy) {
      case PICK_MIN_KEY:
      case PICK_MIN_PERCENT_KEY:
         val = dataSet[dataKey]
            ? dataValue < dataSet[dataKey]
               ? dataValue
               : dataSet[dataKey]
            : dataValue;
         break;
      case PICK_AVG_KEY:
      case PICK_AVG_PERCENT_KEY:
         // sum it up:
         val = dataSet[dataKey] ? dataValue + dataSet[dataKey] : dataValue;
         break;
      case PICK_MAX_KEY:
      case PICK_MAX_PERCENT_KEY:
         val = dataSet[dataKey]
            ? dataValue > dataSet[dataKey]
               ? dataValue
               : dataSet[dataKey]
            : dataValue;
         break;
      default:
         break;
   }

   return val;
};

export const SumAndAverageCalculation = (chartType) => {
   const {
      isODPair = false,
      isMinAvgMax = false,
      isChangeFareGrid = false,
   } = chartType || {};
   const avgSumAndCount = {};

   const generateRecordKey = (record, concatToRecordKey, rKey) => {
      let key = '';
      switch (true) {
         case isODPair:
            key = `${record.origin}_${record.destination}_${record.cabinCode}_${record.carrierCode}_${concatToRecordKey}`;
            break;
         case isMinAvgMax:
            key = `${record.dateValue}_${record.cabinCode}_${record.carrierCode}_${concatToRecordKey}`;
            break;
         case isChangeFareGrid:
            key = `${record.dateValue}_${record.carrierCode}_${record.origin}_${record.destination}_${record.season}_${concatToRecordKey}`;
            break;
         default:
            // when none of the above condition is satisfied or when initialized with no value
            // it will fall back to default. This enables us to give our own key
            key = rKey;
            break;
      }

      return key;
   };

   const addToKey = (recordKey, avgValue) => {
      const numberValue = setNumber(avgValue);
      if (numberValue !== 0) {
         if (avgSumAndCount[recordKey]) {
            avgSumAndCount[recordKey] = {
               sum: avgSumAndCount[recordKey].sum + numberValue,
               count: avgSumAndCount[recordKey].count + 1,
            };
         } else {
            avgSumAndCount[recordKey] = {
               sum: numberValue,
               count: 1,
            };
         }
      }
   };

   // 4th parameter can be used when we want to give our own key
   const addToSum = (record, pickFromObj, pickFromKey, rKey) => {
      const recordKey = generateRecordKey(record, pickFromKey, rKey);
      const avgValue = isEmpty(pickFromObj)
         ? getFareOrFareChangeValueByKey(record, pickFromKey)
         : getFareOrFareChangeValue(record, pickFromObj, pickFromKey);

      if (isIgnore(avgValue)) {
         return;
      }

      addToKey(recordKey, avgValue);
   };

   const getSum = (recordKey) => avgSumAndCount[recordKey];

   const getAvg = (recordKey) => {
      const sumAll = getSum(recordKey);

      return isUndefined(sumAll) || sumAll.sum === 0
         ? 0
         : setNumber(sumAll.sum / sumAll.count);
   };

   const getAll = () => avgSumAndCount;

   return {
      addToSum,
      addToKey,
      getSum,
      getAvg,
      getAll,
   };
};

export const getRespectivePercentKey = (showFaresBy) => {
   switch (showFaresBy) {
      case PICK_MIN_KEY:
         return PICK_MIN_PERCENT_KEY;
      case PICK_AVG_KEY:
         return PICK_AVG_PERCENT_KEY;
      case PICK_MAX_KEY:
         return PICK_MAX_PERCENT_KEY;
      default:
         return showFaresBy;
   }
};
